/**
 * La geometría de una forma en el mundo GPU: relleno sólido y nada más.
 *
 * Es hermana de `CardView` y no una rama suya a propósito. Una forma no tiene
 * markdown, ni textura, ni texto de reemplazo, ni portada de PDF: pasar por
 * `CardView` la obligaba a atravesar todo eso con un `if` en cada paso, y de
 * ahí salían la mitad de sus defectos (ver `docs/plan-formas.md` §1.8).
 *
 * El texto NO va acá: lo dibuja `canvasText.ts`, en el DOM y por encima del
 * canvas. Que sean dos objetos distintos es justamente lo que arregla el bug de
 * la forma invisible — el editor puede tapar el texto sin tapar la figura.
 */

import { Container, Graphics } from "pixi.js";
import { type Card, type ShapeKind, DEFAULT_SHAPE_FILL } from "../model/types.ts";
import { paintSelection, type SelectionChrome } from "./CardView.ts";
import { inkColor } from "./ink.ts";
import { SHAPE_RECT_RADIUS } from "./shapeStyle.ts";

/**
 * Traza la silueta de una forma dentro de un rectángulo. No la rellena: eso lo
 * decide quien llama.
 *
 * Vive suelta porque la usan dos: la forma ya creada y la previa que se dibuja
 * mientras se arrastra. Que sea la misma función es lo que hace que la previa
 * sea de verdad una previa —un rombo se ve rombo mientras se dibuja— y no un
 * rectángulo que después se convierte en otra cosa al soltar.
 */
export function paintShapePath(
  g: Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  kind: ShapeKind,
): void {
  switch (kind) {
    case "ellipse":
      g.ellipse(x + w / 2, y + h / 2, w / 2, h / 2);
      break;
    case "diamond":
      g.poly([x + w / 2, y, x + w, y + h / 2, x + w / 2, y + h, x, y + h / 2]);
      break;
    case "pill":
      g.roundRect(x, y, w, h, Math.min(w, h) / 2);
      break;
    default:
      // El radio hace innecesario un `roundRect` aparte, y se achica en una
      // caja chica para que no se coma la forma entera.
      g.roundRect(x, y, w, h, Math.min(SHAPE_RECT_RADIUS, Math.min(w, h) / 4));
  }
}

export class ShapeView {
  readonly container = new Container();
  private bg = new Graphics();
  /** El cromo de selección. CanvasView lo dibuja en su propia capa (ver
   *  `chromeLayer`) para que una tarjeta de encima no lo tape. */
  readonly selection = new Graphics();

  /** Lo último dibujado, para no repintar un vector que no cambió. */
  private lastFillKey = "";
  private lastSelKey = "";
  selected = false;

  constructor(public card: Card) {
    this.container.eventMode = "none"; // el hit-test lo hace CanvasView
    this.container.addChild(this.bg, this.selection);
  }

  update(card: Card, zoom: number, selected: boolean, chrome: SelectionChrome = {}): void {
    this.card = card;
    this.selected = selected;
    this.container.position.set(card.x, card.y);

    const fillKey = `${card.shape?.kind ?? "rect"}|${card.shape?.fill ?? ""}|${card.width}|${card.height}`;
    if (fillKey !== this.lastFillKey) {
      this.lastFillKey = fillKey;
      this.redraw(card);
    }

    // El recuadro de selección sí depende del zoom (se dibuja a un grosor
    // constante en pantalla), y ahora también del agarre que esté bajo el
    // puntero, así que los dos entran en su firma.
    const selKey = selected
      ? `${card.width}|${card.height}|${zoom}|${chrome.handles ? 1 : 0}|${chrome.hover ?? ""}`
      : "";
    if (selKey !== this.lastSelKey) {
      this.lastSelKey = selKey;
      if (selected) paintSelection(this.selection, card.width, card.height, zoom, chrome);
      else this.selection.clear();
    }
  }

  private redraw(card: Card): void {
    this.bg.clear();
    paintShapePath(this.bg, 0, 0, card.width, card.height, card.shape?.kind ?? "rect");
    // Sólido y sin borde: el color elegido es el color que se ve. El contraste
    // del texto lo resuelve `textColorFor` contra este mismo relleno.
    this.bg.fill(inkColor(card.shape?.fill ?? DEFAULT_SHAPE_FILL));
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
